import { useEffect, useEffectEvent, useRef, useState } from "react"
import { Navigate, useNavigate } from "react-router-dom"
import { Activity, ChevronDown, RefreshCw, Search } from "lucide-react"
import { toast } from "sonner"
import { StepNavigation } from "@/components/activation/StepNavigation"
import { decimalOnly, digitsOnly, INPUT_PATTERNS } from "@/components/activation/input-sanitizers"
import { primaryActionClasses } from "@/components/activation/styles"
import { useActivation } from "@/lib/activation-context"
import { cn } from "@/lib/utils"

const SECTIONS = [
  { id: "residential", label: "Residential" },
  { id: "commercial", label: "Commercial" },
  { id: "hmo", label: "HMO" },
]

const MARKET_SECTIONS = ["residentialValue", "residentialRent", "commercial", "hmo"]
const SECTION_FIELDS = {
  residentialValue: ["soldPrice", "sqft"],
  residentialRent: ["rent"],
  commercial: ["rent"],
  hmo: ["rent"],
}
const PROTECTED_QUALITIES = ["edited_by_you", "updated_market_data_available"]
const QUALITY_LABELS = {
  baseline_available: "Baseline available",
  limited_baseline: "Limited baseline",
  manual_input_required: "Manual input required",
  edited_by_you: "Edited by you",
  updated_market_data_available: "Updated market data available",
}
const SCAN_STAGES = [
  "Scanning selected areas",
  "Checking rent and value baselines",
  "Comparing market data",
  "Preparing manual review fields",
]
const PROGRESS_STATUS_LABELS = {
  pending: "Pending",
  scanning: "Scanning",
  complete: "Complete",
  needs_review: "Needs review",
  failed: "Failed",
}
const SCAN_STAGE_INTERVAL_MS = 1800
const SCAN_POLL_INTERVAL_MS = 2000

export function DynamicsStep() {
  const { step3, step4, saveStep, scanMarkets, getMarketScanRun, serverError } = useActivation()
  const navigate = useNavigate()

  const [marketEdits, setMarketEdits] = useState({})
  const [isScanning, setIsScanning] = useState(false)
  const [scanRun, setScanRun] = useState(null)
  const [scanStageIndex, setScanStageIndex] = useState(null)
  const [scanProgressIndex, setScanProgressIndex] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const [activePostcodeTab, setActivePostcodeTab] = useState(0)
  const [expandedSections, setExpandedSections] = useState(() => {
    const init = {}
    step3.selectedPostcodes.forEach((pc) => {
      init[pc] = { residential: true, commercial: false, hmo: false }
    })
    return init
  })
  const progressIntervalRef = useRef(null)
  const scanPollIntervalRef = useRef(null)
  const scanPollActiveRef = useRef(false)
  const restoredScanRun = step4.marketScanRun
  const activeScanRun = scanRun || restoredScanRun
  const scanInProgress = Boolean(activeScanRun && !runFinished(activeScanRun))
  const scanActive = isScanning || scanInProgress
  const activeScanRunId = activeScanRun?.id
  const activeScanRunStatus = activeScanRun?.status
  const pollActiveScanRun = useEffectEvent((runId) => {
    pollRun(runId)
  })

  useEffect(() => () => {
    window.clearInterval(progressIntervalRef.current)
    window.clearInterval(scanPollIntervalRef.current)
  }, [])

  useEffect(() => {
    if (!activeScanRunId || runFinished({ status: activeScanRunStatus })) return undefined

    window.clearInterval(scanPollIntervalRef.current)
    scanPollIntervalRef.current = window.setInterval(() => {
      pollActiveScanRun(activeScanRunId)
    }, SCAN_POLL_INTERVAL_MS)

    return () => {
      window.clearInterval(scanPollIntervalRef.current)
      scanPollIntervalRef.current = null
    }
  }, [activeScanRunId, activeScanRunStatus])

  const marketData = mergeScannedMarketData(marketEdits, step4.marketData)
  const scannedPostcodes = step3.selectedPostcodes.filter((postcode) => hasMarketRows(marketData[postcode]))
  const missingPostcodes = step3.selectedPostcodes.filter((postcode) => !hasMarketRows(marketData[postcode]))
  const marketDataScanned = scannedPostcodes.length > 0
  const allPostcodesScanned = missingPostcodes.length === 0
  const firstScannedPostcodeIndex = step3.selectedPostcodes.findIndex((postcode) => hasMarketRows(marketData[postcode]))
  const selectedPostcodeTab = scanActive &&
    !hasMarketRows(marketData[step3.selectedPostcodes[activePostcodeTab]]) &&
    firstScannedPostcodeIndex >= 0
    ? firstScannedPostcodeIndex
    : activePostcodeTab
  const activePostcode = step3.selectedPostcodes[selectedPostcodeTab]
  const activeData = marketData[activePostcode]
  const activeStatus = postcodeStatus(activeData)
  const scanDisabled = scanActive || (marketDataScanned && allPostcodesScanned)
  const showActivePostcodeData = Boolean(activeData && activeStatus !== "missing")

  if (!step3.selectedPostcodes.length) return <Navigate to="/activation/step/3" replace />

  const scan = async ({ quietCompleted = false } = {}) => {
    setScanRun(null)
    setSubmitError(null)
    if (!quietCompleted) {
      setIsScanning(true)
      startProgressLoop()
    }

    try {
      const payload = await scanMarkets({ force: false })
      if (quietCompleted && payload.run && runFinished(payload.run)) {
        setScanRun(payload.run)
        completeScan(payload)
        toast.success("Market data already up to date.")
        return
      }
      if (quietCompleted) {
        setIsScanning(true)
        startProgressLoop()
      }
      if (payload.run) {
        setScanRun(payload.run)
        if (runFinished(payload.run)) {
          completeScan(payload)
        }
      } else {
        completeScan({ activation: payload })
      }
    } catch (error) {
      setSubmitError(error.message)
      stopProgressLoop()
      setIsScanning(false)
    }
  }

  const refresh = () => {
    scan({ quietCompleted: true })
  }

  const toggleSection = (postcode, section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [postcode]: { ...prev[postcode], [section]: !prev[postcode]?.[section] },
    }))
  }

  const updateMarketValue = (postcode, section, idx, field, value) => {
    updateMarketRow(postcode, section, idx, (row) => markEditedField(row, field, value))
  }

  const keepManualValue = (postcode, section, idx, field) => {
    updateMarketRow(postcode, section, idx, (row) => keepEditedField(row, field))
  }

  const applyLatestValue = (postcode, section, idx, field) => {
    updateMarketRow(postcode, section, idx, (row) => applyLatestField(row, field))
  }

  const updateMarketRow = (postcode, section, idx, updateRow) => {
    setMarketEdits((prev) => {
      const base = editableMarketData(prev, step4.marketData)
      const next = { ...base, [postcode]: { ...base[postcode] } }
      next[postcode][section] = next[postcode][section].map((row, i) =>
        i === idx ? updateRow(row) : row,
      )
      return next
    })
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    if (!marketDataScanned) return
    setIsSubmitting(true)
    setSubmitError(null)
    try {
      await saveStep(4, { marketData, microscapes: step4.microscapes, unitTypes: step4.unitTypes })
      navigate("/activation/step/5")
    } catch (error) {
      setSubmitError(error.message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form onSubmit={onSubmit} noValidate className="animate-in fade-in slide-in-from-bottom-4 duration-300">
      <div className="mx-auto w-full max-w-7xl px-6 py-12 sm:px-8 lg:px-12">
        <div className="mb-8">
          <p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">STEP 4 OF 5</p>
          <h1 className="mb-2 text-3xl font-bold tracking-tight text-foreground">Capture Area Dynamics</h1>
          <p className="mb-6 text-base text-muted-foreground">
            Scan each selected postcode area and build editable market baselines for sourcing, concepting, and appraisal.
          </p>

          <div>
            <button
              type="button"
              onClick={() => scan()}
              disabled={scanDisabled}
              className={primaryActionClasses(scanDisabled, "flex items-center gap-2")}
            >
              <Search className={cn("h-4 w-4", scanActive ? "animate-spin" : "")} />
              {scanActive
                ? "Scanning..."
                : marketDataScanned && allPostcodesScanned
                  ? "Market Data Scanned"
                  : "Scan Market Data"}
            </button>
          </div>
          {missingPostcodes.length > 0 && marketDataScanned ? (
            <p className="mt-3 text-sm text-muted-foreground">
              Missing market data for <span className="font-mono text-foreground">{missingPostcodes.join(", ")}</span>
            </p>
          ) : null}
          {scanActive ? (
            <ScanProgress
              run={activeScanRun}
              stageIndex={scanStageIndex ?? 0}
              progressIndex={scanProgressIndex ?? 0}
            />
          ) : null}
        </div>

        <div className="mb-8">
          <div className="flex gap-2 border-b border-border">
            {step3.selectedPostcodes.map((pc, index) => {
              const status = postcodeStatus(marketData[pc])
              const disabled = scanActive && !hasMarketRows(marketData[pc])
              return (
                <button
                  key={pc}
                  type="button"
                  disabled={disabled}
                  onClick={() => {
                    if (disabled) return

                    setActivePostcodeTab(index)
                  }}
                  className={cn(
                    "relative flex items-center gap-2 px-4 py-3 text-sm font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-50",
                    selectedPostcodeTab === index ? "text-primary" : "text-muted-foreground hover:text-foreground",
                    disabled && "hover:text-muted-foreground",
                  )}
                >
                  {pc}
                  <span
                    className={cn(
                      "h-1.5 w-1.5 rounded-full",
                      status === "loaded" && "bg-primary",
                      status === "partial" && "bg-chart-4",
                      status === "missing" && "bg-muted-foreground/40",
                    )}
                  />
                  {selectedPostcodeTab === index ? (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                  ) : null}
                </button>
              )
            })}
          </div>
        </div>

        {showActivePostcodeData ? (
          <div className="space-y-6">
            <div className="flex items-center justify-between rounded-xl border border-border bg-card p-4">
              <div className="flex items-center gap-4">
                <h2 className="text-lg font-bold text-foreground">{activePostcode}</h2>
                <span className={statusBadgeClasses(activeStatus)}>
                  {activeStatus === "loaded" ? "Data Loaded" : "Partial Data"}
                </span>
              </div>
              <button
                type="button"
                onClick={refresh}
                disabled={scanActive}
                className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <RefreshCw className={cn("h-4 w-4", scanActive ? "animate-spin" : "")} />
                Refresh Data
              </button>
            </div>
            <MarketDataNotice data={activeData} />

            {SECTIONS.map(({ id, label }) => {
              const expanded = expandedSections[activePostcode]?.[id]
              return (
                <div key={id} className="overflow-hidden rounded-xl border border-border bg-card">
                  <button
                    type="button"
                    onClick={() => toggleSection(activePostcode, id)}
                    aria-expanded={expanded}
                    className="flex w-full items-center justify-between p-4 transition-colors hover:bg-muted/50"
                  >
                    <h3 className="text-sm font-bold uppercase tracking-widest text-foreground">{label}</h3>
                    <ChevronDown
                      className={cn("h-5 w-5 text-muted-foreground transition-transform", expanded ? "rotate-180" : "")}
                    />
                  </button>
                  {expanded ? (
                    <SectionBody
                      section={id}
                      data={activeData}
                      onUpdate={(sec, idx, field, value) => updateMarketValue(activePostcode, sec, idx, field, value)}
                      onKeepManual={(sec, idx, field) => keepManualValue(activePostcode, sec, idx, field)}
                      onUseLatest={(sec, idx, field) => applyLatestValue(activePostcode, sec, idx, field)}
                    />
                  ) : null}
                </div>
              )
            })}
          </div>
        ) : null}

        {!marketDataScanned && scanActive ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border bg-muted/30 p-12 text-center">
            <Activity className="mb-4 h-12 w-12 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">
              Market baselines will appear here as each postcode completes.
            </p>
          </div>
        ) : null}

        {!marketDataScanned && !scanActive ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border bg-muted/30 p-12 text-center">
            <Activity className="mb-4 h-12 w-12 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">
              Click &ldquo;Scan Market Data&rdquo; to generate market baselines for your selected postcode areas
            </p>
          </div>
        ) : null}

        {marketDataScanned && activeStatus === "missing" ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border bg-muted/30 p-12 text-center">
            <Activity className="mb-4 h-12 w-12 text-muted-foreground/40" />
            <p className="text-sm font-semibold text-foreground">No cached market data for {activePostcode}</p>
            <p className="mt-1 text-sm text-muted-foreground">
              Run the scan again to fetch missing cached endpoints for this postcode.
            </p>
          </div>
        ) : null}

        <StepNavigation
          stepNumber={4}
          canContinue={marketDataScanned && !scanActive}
          isSubmitting={isSubmitting}
          continueLabel="Lock Market Baselines"
          onBack={() => navigate("/activation/step/3")}
        />
        {submitError || serverError ? (
          <p className="mt-4 text-center text-sm text-destructive">{submitError || serverError}</p>
        ) : null}
      </div>
    </form>
  )

  function startProgressLoop() {
    window.clearInterval(progressIntervalRef.current)
    setScanStageIndex(0)
    setScanProgressIndex(0)
    progressIntervalRef.current = window.setInterval(() => {
      setScanStageIndex((prev) => ((prev ?? 0) + 1) % SCAN_STAGES.length)
      setScanProgressIndex((prev) => Math.min((prev ?? 0) + 1, SCAN_STAGES.length - 1))
    }, SCAN_STAGE_INTERVAL_MS)
  }

  function stopProgressLoop() {
    window.clearInterval(progressIntervalRef.current)
    progressIntervalRef.current = null
    setScanStageIndex(null)
    setScanProgressIndex(null)
  }

  async function pollRun(runId) {
    if (scanPollActiveRef.current) return

    scanPollActiveRef.current = true
    try {
      const payload = await getMarketScanRun(runId)
      setScanRun(payload.run)
      if (runFinished(payload.run)) completeScan(payload)
    } catch (error) {
      setSubmitError(error.message)
      stopRunPolling()
      stopProgressLoop()
      setIsScanning(false)
    } finally {
      scanPollActiveRef.current = false
    }
  }

  function completeScan(payload) {
    if (payload.run?.status === "failed") {
      setSubmitError("Market scan failed. Please retry.")
    }
    if (payload.activation) {
      setMarketEdits((prev) => mergeScannedMarketData(prev, payload.activation.step4.marketData, { markUpdates: true }))
    }
    stopRunPolling()
    stopProgressLoop()
    setIsScanning(false)
  }

  function stopRunPolling() {
    window.clearInterval(scanPollIntervalRef.current)
    scanPollIntervalRef.current = null
  }
}

function ScanProgress({ run, stageIndex, progressIndex }) {
  const currentStage = SCAN_STAGES[stageIndex] || SCAN_STAGES[0]
  const progress = run ? runProgressState(run) : null
  const progressPercent = progress?.percent || fallbackProgressPercent(progressIndex)

  return (
    <div role="status" aria-live="polite" className="mt-4 max-w-2xl rounded-xl border border-border bg-card p-4">
      <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
        <Activity className="h-4 w-4 animate-pulse text-primary" />
        Sentinel is scanning market data
      </div>
      {progress ? (
        <div className="space-y-1">
          <p className="text-sm font-semibold text-foreground">
            {progress.complete ? progress.completeTitle : "Sentinel is scanning market data"}
          </p>
          <p className="text-sm text-muted-foreground">{progress.message}</p>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{currentStage}</p>
      )}
      <div className="mt-4 h-2 overflow-hidden rounded-full bg-muted">
        <div className="activation-scan-progress-fill" style={{ width: `${progressPercent}%` }}>
          <div className="activation-scan-progress-strip absolute inset-y-0 w-1/2" />
        </div>
      </div>
      {progress ? (
        <ProgressDetails progress={progress} currentStage={currentStage} />
      ) : (
        <div className="mt-4 space-y-2">
          {SCAN_STAGES.map((stage, index) => (
            <div key={stage} className="flex items-center gap-3 text-sm">
              <span
                className={cn(
                  "h-2 w-2 rounded-full",
                  index <= stageIndex ? "bg-primary" : "bg-muted-foreground/30",
                )}
              />
              <span className={cn(index <= stageIndex ? "text-foreground" : "text-muted-foreground")}>{stage}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function ProgressDetails({ progress, currentStage }) {
  return (
    <div className="mt-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {progress.scannedCount} of {progress.total} postcode areas scanned
      </p>
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
        {progress.statuses.map(({ postcode, status }) => (
          <div key={postcode} className="flex items-center justify-between rounded-lg border border-border bg-muted/30 px-3 py-2">
            <span className="font-mono text-sm font-semibold text-foreground">{postcode}</span>
            <span className="flex items-center gap-2 text-xs font-semibold">
              <span className={progressStatusDotClasses(status)} />
              <span className={progressStatusTextClasses(status)}>{PROGRESS_STATUS_LABELS[status]}</span>
            </span>
          </div>
        ))}
      </div>
      <p className="mt-4 text-sm text-muted-foreground">{currentStage}</p>
    </div>
  )
}

function MarketDataNotice({ data }) {
  const summary = marketDataSummary(data)
  if (!summary.manual && !summary.limited && !summary.updated) return null

  return (
    <div role="note" className="rounded-xl border border-border bg-muted/30 p-4 text-sm">
      <p className="font-semibold text-foreground">Some baselines need manual review.</p>
      <div className="mt-2 space-y-1 text-muted-foreground">
        {summary.manual ? (
          <p>Manual input required - limited market evidence available for {summary.manual} fields.</p>
        ) : null}
        {summary.limited ? (
          <p>Limited baseline available for {summary.limited} fields. Review before using in appraisal.</p>
        ) : null}
        {summary.updated ? (
          <p>Updated market data is available for {summary.updated} edited fields. Keep yours or use latest.</p>
        ) : null}
      </div>
    </div>
  )
}

function SectionBody({ section, data, onUpdate, onKeepManual, onUseLatest }) {
  if (section === "residential") {
    return (
      <div className="space-y-6 border-t border-border p-6">
        <EditableTable
          heading="Value Baselines"
          columns={["Property Type", "Sold Price", "£/sqft"]}
          rows={data.residentialValue || []}
          readFields={["type"]}
          editFields={[
            {
              field: "soldPrice",
              mode: "integer",
              onChange: (i, v) => onUpdate("residentialValue", i, "soldPrice", v),
              onKeepManual: (i) => onKeepManual("residentialValue", i, "soldPrice"),
              onUseLatest: (i) => onUseLatest("residentialValue", i, "soldPrice"),
            },
            {
              field: "sqft",
              mode: "decimal",
              onChange: (i, v) => onUpdate("residentialValue", i, "sqft", v),
              onKeepManual: (i) => onKeepManual("residentialValue", i, "sqft"),
              onUseLatest: (i) => onUseLatest("residentialValue", i, "sqft"),
            },
          ]}
        />
        <EditableTable
          heading="Rent Baselines"
          columns={["Property Type", "Rent (PCM)"]}
          rows={data.residentialRent || []}
          readFields={["type"]}
          editFields={[{
            field: "rent",
            mode: "integer",
            onChange: (i, v) => onUpdate("residentialRent", i, "rent", v),
            onKeepManual: (i) => onKeepManual("residentialRent", i, "rent"),
            onUseLatest: (i) => onUseLatest("residentialRent", i, "rent"),
          }]}
        />
      </div>
    )
  }
  if (section === "commercial") {
    return (
      <div className="border-t border-border p-6">
        <EditableTable
          columns={["Property Type", "Rent £/sqft"]}
          rows={data.commercial || []}
          readFields={["type"]}
          editFields={[{
            field: "rent",
            mode: "decimal",
            onChange: (i, v) => onUpdate("commercial", i, "rent", v),
            onKeepManual: (i) => onKeepManual("commercial", i, "rent"),
            onUseLatest: (i) => onUseLatest("commercial", i, "rent"),
          }]}
        />
      </div>
    )
  }
  return (
    <div className="border-t border-border p-6">
      <EditableTable
        columns={["Room Type", "Rent (PCM)"]}
        rows={data.hmo || []}
        readFields={["type"]}
        editFields={[{
          field: "rent",
          mode: "integer",
          onChange: (i, v) => onUpdate("hmo", i, "rent", v),
          onKeepManual: (i) => onKeepManual("hmo", i, "rent"),
          onUseLatest: (i) => onUseLatest("hmo", i, "rent"),
        }]}
      />
    </div>
  )
}

function EditableTable({ heading, columns, rows, readFields, editFields }) {
  if (!rows.length) {
    return (
      <div>
        {heading ? (
          <h4 className="mb-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">{heading}</h4>
        ) : null}
        <div className="rounded-lg border border-dashed border-border bg-muted/30 p-4 text-sm text-muted-foreground">
          No baseline rows returned for this section.
        </div>
      </div>
    )
  }

  return (
    <div>
      {heading ? (
        <h4 className="mb-3 text-xs font-bold uppercase tracking-widest text-muted-foreground">{heading}</h4>
      ) : null}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              {columns.map((col) => (
                <th key={col} className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={row.type} className="border-b border-border/50">
                {readFields.map((f) => (
                  <td key={f} className="px-3 py-2 text-sm text-foreground">{row[f]}</td>
                ))}
                {editFields.map(({ field, mode = "integer", onChange, onKeepManual, onUseLatest }) => (
                  <td key={field} className="px-3 py-2">
                    <div className="space-y-1">
                      <input
                        type="text"
                        inputMode={mode === "decimal" ? "decimal" : "numeric"}
                        pattern={INPUT_PATTERNS[mode]}
                        autoComplete="off"
                        value={row[field] || ""}
                        onChange={(e) => onChange(idx, sanitizeMarketNumber(e.target.value, mode))}
                        className="w-full rounded border border-border bg-background px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                      />
                      <FieldQualityControls
                        row={row}
                        field={field}
                        onKeepManual={() => onKeepManual(idx)}
                        onUseLatest={() => onUseLatest(idx)}
                      />
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function hasUsableMarketData(data) {
  if (!data) return false

  return MARKET_SECTIONS.some((section) =>
    (data[section] || []).some((row) => rowHasValue(section, row)),
  )
}

function hasMarketRows(data) {
  if (!data) return false

  return MARKET_SECTIONS.some((section) => (data[section] || []).length > 0)
}

function postcodeStatus(data) {
  if (!hasMarketRows(data)) return "missing"
  if (!hasUsableMarketData(data) || hasManualReviewFields(data)) return "partial"

  const populatedSections = MARKET_SECTIONS.filter((section) =>
    (data[section] || []).some((row) => rowHasValue(section, row)),
  )
  return populatedSections.length === MARKET_SECTIONS.length ? "loaded" : "partial"
}

function rowHasValue(section, row) {
  return SECTION_FIELDS[section].some((field) => String(row[field] || "").trim().length > 0)
}

function hasManualReviewFields(data) {
  return MARKET_SECTIONS.some((section) =>
    (data[section] || []).some((row) =>
      SECTION_FIELDS[section].some((field) => manualReviewField(row, field)),
    ),
  )
}

function manualReviewField(row, field) {
  const quality = fieldQuality(row, field)

  return quality === "limited_baseline" ||
    quality === "manual_input_required" ||
    quality === "updated_market_data_available" ||
    String(row[field] || "").trim().length === 0
}

function statusBadgeClasses(status) {
  return cn(
    "rounded border px-2 py-1 text-xs font-semibold",
    status === "loaded"
      ? "border-primary/20 bg-primary/10 text-primary"
      : "border-chart-4/20 bg-muted text-chart-4",
  )
}

function runProgressState(run) {
  const statuses = run.postcodes || []
  const total = run.totalPostcodes || statuses.length || 1
  const scannedCount = run.completedPostcodes ?? terminalProgressCount(statuses)
  const complete = runFinished(run)
  const current = run.currentPostcode || statuses.find(({ status }) => status === "scanning")?.postcode
  const nextPending = statuses.find(({ status }) => status === "pending")?.postcode

  return {
    complete,
    completeTitle: run.status === "failed" ? "Market scan failed" : "Market scan complete",
    message: runProgressMessage(run, current || nextPending),
    percent: complete ? 100 : Math.max(8, Math.round((scannedCount / total) * 100)),
    scannedCount,
    statuses,
    total,
  }
}

function runProgressMessage(run, postcode) {
  if (run.status === "failed") return "Sentinel could not complete this scan. Please retry."
  if (run.status === "partial") return "Some baselines need manual review."
  if (run.status === "succeeded") return "Market baselines are ready."

  return postcode ? `Scanning ${postcode}` : "Preparing selected areas"
}

function terminalProgressCount(statuses) {
  return statuses.filter(({ status }) => ["complete", "needs_review", "failed"].includes(status)).length
}

function fallbackProgressPercent(progressIndex) {
  return Math.min(100, Math.max(8, (progressIndex + 1) * 25))
}

function runFinished(run) {
  return ["succeeded", "partial", "failed"].includes(run?.status)
}

function progressStatusDotClasses(status) {
  return cn(
    "h-2 w-2 rounded-full",
    status === "complete" && "bg-primary",
    status === "needs_review" && "bg-chart-4",
    status === "scanning" && "bg-chart-4",
    status === "failed" && "bg-destructive",
    status === "pending" && "bg-muted-foreground/30",
  )
}

function progressStatusTextClasses(status) {
  return cn(
    status === "complete" && "text-primary",
    status === "needs_review" && "text-chart-4",
    status === "scanning" && "text-foreground",
    status === "failed" && "text-destructive",
    status === "pending" && "text-muted-foreground",
  )
}

function FieldQualityBadge({ quality }) {
  const normalized = QUALITY_LABELS[quality] ? quality : "manual_input_required"

  return (
    <span className={qualityBadgeClasses(normalized)}>
      {QUALITY_LABELS[normalized]}
    </span>
  )
}

function FieldQualityControls({ row, field, onKeepManual, onUseLatest }) {
  const update = fieldUpdate(row, field)

  return (
    <div className="space-y-1">
      <FieldQualityBadge quality={fieldQuality(row, field)} />
      {update ? (
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span>Latest: {update.value}</span>
          <button type="button" onClick={onKeepManual} className="font-semibold text-foreground hover:text-primary">
            Keep mine
          </button>
          <button type="button" onClick={onUseLatest} className="font-semibold text-primary hover:text-foreground">
            Use latest
          </button>
        </div>
      ) : null}
    </div>
  )
}

function qualityBadgeClasses(quality) {
  return cn(
    "inline-flex rounded border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
    quality === "baseline_available" && "border-primary/20 bg-primary/10 text-primary",
    quality === "limited_baseline" && "border-chart-4/20 bg-muted text-chart-4",
    quality === "manual_input_required" && "border-destructive/20 bg-muted text-destructive",
    quality === "edited_by_you" && "border-border bg-accent text-accent-foreground",
    quality === "updated_market_data_available" && "border-chart-4/20 bg-muted text-chart-4",
  )
}

function fieldQuality(row, field) {
  if (row.quality?.[field]) return row.quality[field]
  if (String(row[field] || "").trim().length > 0) return "baseline_available"

  return "manual_input_required"
}

function markEditedField(row, field, value) {
  const nextUpdates = removeFieldMetadata(row.updates, field)
  const quality = String(value || "").trim().length > 0 ? "edited_by_you" : "manual_input_required"
  const next = {
    ...row,
    [field]: value,
    quality: {
      ...(row.quality || {}),
      [field]: quality,
    },
  }

  return applyUpdates(next, nextUpdates)
}

function keepEditedField(row, field) {
  const nextUpdates = removeFieldMetadata(row.updates, field)
  const next = {
    ...row,
    quality: {
      ...(row.quality || {}),
      [field]: "edited_by_you",
    },
  }

  return applyUpdates(next, nextUpdates)
}

function applyLatestField(row, field) {
  const update = fieldUpdate(row, field)
  if (!update) return row

  const nextUpdates = removeFieldMetadata(row.updates, field)
  const next = {
    ...row,
    [field]: update.value,
    quality: {
      ...(row.quality || {}),
      [field]: update.quality || "baseline_available",
    },
  }

  return applyUpdates(next, nextUpdates)
}

function applyUpdates(row, updates) {
  const next = { ...row }
  if (updates && Object.keys(updates).length > 0) {
    next.updates = updates
  } else {
    delete next.updates
  }
  return next
}

function removeFieldMetadata(metadata, field) {
  if (!metadata) return undefined

  const next = { ...metadata }
  delete next[field]
  return Object.keys(next).length > 0 ? next : undefined
}

function fieldUpdate(row, field) {
  return row.updates?.[field]
}

function editableMarketData(current, scanned) {
  return {
    ...(scanned || {}),
    ...(current || {}),
  }
}

function mergeScannedMarketData(current, scanned, options = {}) {
  const next = { ...(scanned || {}) }

  Object.entries(current || {}).forEach(([postcode, currentData]) => {
    const scannedData = scanned?.[postcode]
    next[postcode] = scannedData ? mergePostcodeMarketData(currentData, scannedData, options) : currentData
  })

  return next
}

function mergePostcodeMarketData(currentData, scannedData, options) {
  const next = { ...scannedData }

  MARKET_SECTIONS.forEach((section) => {
    next[section] = mergeSectionRows(currentData?.[section] || [], scannedData?.[section] || [], section, options)
  })

  return next
}

function mergeSectionRows(currentRows, scannedRows, section, options) {
  if (!scannedRows.length) return currentRows

  return scannedRows.map((scannedRow, index) => {
    const currentRow = matchingCurrentRow(currentRows, scannedRow, section, index)
    if (!currentRow) return scannedRow

    return mergeRowFields(currentRow, scannedRow, section, options)
  })
}

function matchingCurrentRow(currentRows, scannedRow, section, index) {
  const exactMatch = currentRows.find((row) => row.type === scannedRow.type)
  if (exactMatch || section !== "residentialRent") return exactMatch || currentRows[index]

  const bedroom = rentBedroom(scannedRow.type)
  const editedBedroomRows = currentRows.filter((row) => (
    rentBedroom(row.type) === bedroom &&
    PROTECTED_QUALITIES.includes(fieldQuality(row, "rent"))
  ))

  return editedBedroomRows.length === 1 ? editedBedroomRows[0] : undefined
}

function rentBedroom(type) {
  return String(type || "").match(/\b(\d+)[- ]?bed\b/i)?.[1]
}

function mergeRowFields(currentRow, scannedRow, section, { markUpdates = false } = {}) {
  const next = { ...scannedRow, quality: { ...(scannedRow.quality || {}) } }

  SECTION_FIELDS[section].forEach((field) => {
    const currentQuality = fieldQuality(currentRow, field)
    if (!PROTECTED_QUALITIES.includes(currentQuality)) return

    const currentValue = currentRow[field] ?? ""
    const scannedValue = scannedRow[field] ?? ""
    const quality = markUpdates && scannedValue && scannedValue !== currentValue
      ? "updated_market_data_available"
      : currentQuality
    next[field] = currentValue
    next.quality[field] = quality
    if (quality === "updated_market_data_available") {
      next.updates = {
        ...(next.updates || {}),
        [field]: {
          value: scannedValue,
          quality: fieldQuality(scannedRow, field),
        },
      }
    }
  })

  return next
}

function marketDataSummary(data) {
  return MARKET_SECTIONS.reduce((summary, section) => {
    const rows = data[section] || []
    rows.forEach((row) => {
      SECTION_FIELDS[section].forEach((field) => {
        const quality = fieldQuality(row, field)
        if (quality === "manual_input_required") summary.manual += 1
        if (quality === "limited_baseline") summary.limited += 1
        if (quality === "updated_market_data_available") summary.updated += 1
      })
    })
    return summary
  }, { manual: 0, limited: 0, updated: 0 })
}

function sanitizeMarketNumber(value, mode) {
  if (mode === "decimal") return decimalOnly(value)

  return digitsOnly(value)
}
